// Renders the global toast queue from useUIStore. Mount once at the
// application root.
//
// Toasts are queued via `useUIStore.showToast({ type, title, message })`
// from anywhere in the app. The store auto-dismisses each one after its
// `duration` (default 5s); this component is purely the visual layer.
//
// Stacking: latest toasts appear at the bottom of the stack, animating
// in from the right. The container is fixed top-right to stay clear of
// the cart sidebar (right side, but lower than the header) and the
// floating dev panels (bottom).

import { useEffect, useState } from 'react'
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from 'lucide-react'
import { useUIStore } from '@/stores/uiStore'

const TYPE_STYLES: Record<
  'success' | 'error' | 'warning' | 'info',
  { ring: string; bg: string; iconColor: string; Icon: typeof CheckCircle2 }
> = {
  success: { ring: 'ring-green-200', bg: 'bg-white', iconColor: 'text-green-500', Icon: CheckCircle2 },
  error: { ring: 'ring-red-200', bg: 'bg-white', iconColor: 'text-red-500', Icon: AlertCircle },
  warning: { ring: 'ring-amber-200', bg: 'bg-white', iconColor: 'text-amber-500', Icon: AlertTriangle },
  info: { ring: 'ring-blue-200', bg: 'bg-white', iconColor: 'text-blue-500', Icon: Info },
}

export function Toaster() {
  const toasts = useUIStore((s) => s.toasts)
  const dismissToast = useUIStore((s) => s.dismissToast)

  if (toasts.length === 0) return null

  return (
    <div
      className="fixed top-20 right-4 z-[100] flex flex-col gap-2 pointer-events-none"
      role="region"
      aria-label="Notifications"
    >
      {toasts.map((t) => (
        <ToastCard
          key={t.id}
          type={t.type}
          title={t.title}
          message={t.message}
          onDismiss={() => dismissToast(t.id)}
        />
      ))}
    </div>
  )
}

interface ToastCardProps {
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message?: string
  onDismiss: () => void
}

function ToastCard({ type, title, message, onDismiss }: ToastCardProps) {
  // One-frame mount delay so the slide-in transition runs (going from
  // translate-x-2 + opacity-0 → translate-x-0 + opacity-100). Without
  // it the element would render at its final position and skip the
  // animation entirely.
  const [shown, setShown] = useState(false)
  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(true))
    return () => cancelAnimationFrame(id)
  }, [])

  const { ring, bg, iconColor, Icon } = TYPE_STYLES[type]

  return (
    <div
      className={`pointer-events-auto min-w-[280px] max-w-sm ${bg} ${ring} ring-1 shadow-lg rounded-lg p-3 flex items-start gap-3 transition-all duration-200 ${
        shown ? 'translate-x-0 opacity-100' : 'translate-x-2 opacity-0'
      }`}
    >
      <Icon size={20} className={`${iconColor} flex-shrink-0 mt-0.5`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 leading-tight">{title}</p>
        {message && (
          <p className="text-sm text-gray-600 mt-0.5 leading-snug break-words">{message}</p>
        )}
      </div>
      <button
        onClick={onDismiss}
        className="flex-shrink-0 p-1 -m-1 text-gray-400 hover:text-gray-600 rounded transition-colors cursor-pointer"
        aria-label="Dismiss"
      >
        <X size={16} />
      </button>
    </div>
  )
}
